/**
 * Persistent assistant entry point: a floating launcher that opens the chat
 * panel as a right-side drawer (full-screen on small viewports), with focus
 * trapping and a resizable width. Owns the chat state (via useAssistantChat) so
 * the conversation survives the drawer closing — host-shell concerns (the user,
 * navigation, balance, money formatting, the graph renderer, and the workflow-
 * mutation signal) are injected.
 *
 * Mount inside an <AssistantClientProvider> (transport) and an
 * <AssistantLauncherProvider> (open/seed state).
 */

import { MessageSquare } from "lucide-react";
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useEffect,
  useRef,
} from "react";
import { type ToolDetailRenderers } from "../web-react";
import { AssistantPanel, type AssistantPanelProps } from "./AssistantPanel";
import { useAssistantLauncher } from "./launcher";
import { ResizeHandle } from "./ResizeHandle";
import type {
  AssistantTranscriptView,
  ConfirmedResult,
  ConnectionRequirement,
  ConnectRequirementResult,
} from "./types";
import { useAssistantChat } from "./useAssistantChat";
import { useIsDesktop, usePanelWidth } from "./usePanelPrefs";

export interface AssistantDockProps {
  /** The signed-in user this conversation belongs to (null when signed out). */
  userId: string | null;
  /** Host navigation for error CTAs and connect targets. */
  navigate?: (path: string) => void;
  balanceUsd?: number | null;
  formatMoney?: (usd: number | null) => string;
  /** Render workflow YAML as a node graph in a proposal card. */
  renderGraph?: (yaml: string) => ReactNode;
  /** Render the brand icon for a proposal requirement's integration provider.
   *  The host owns the provider→icon mapping (its integrations catalog); when
   *  absent, the card falls back to its built-in provider mark. */
  renderProviderIcon?: (provider: string) => ReactNode;
  /** Called after a workflow-mutating tool is confirmed (host re-fetches its list). */
  onWorkflowMutation?: () => void;
  /** In-place connect handler for a proposal's integration requirements. The host
   *  runs its own connect flow (OAuth popup, api-key modal, app install) and
   *  resolves whether the requirement is now satisfied; the proposal card then
   *  flips it to connected. Host-agnostic — when omitted, the card falls back to
   *  navigating the requirement's connect target via `navigate`. */
  onConnectRequirement?: (
    requirement: ConnectionRequirement,
  ) => Promise<ConnectRequirementResult>;
  /** Markdown renderer for assistant message content (plain text when absent). */
  renderMarkdown?: (content: string) => ReactNode;
  /** Per-tool custom detail renderers for expanded tool cards in the transcript. */
  toolRenderers?: ToolDetailRenderers;
  /** Render a prominent card for a CONFIRMED tool's result (e.g. a one-time
   *  API-key reveal for `create_api_key`), shown inline after the action's status
   *  line. The result's `output` may carry a one-time secret — see
   *  {@link ConfirmedResult} for the never-persist/never-to-model contract. */
  renderConfirmedResult?: (result: ConfirmedResult) => ReactNode;
  /** Swap the conversation rendering for a host-supplied renderer (see
   *  {@link AssistantPanelProps.renderTranscript}); the dock chrome, composer,
   *  transport, and proposal flow stay owned by the panel. */
  renderTranscript?: (view: AssistantTranscriptView) => ReactNode;
  /** Opt-in attachment surface for the dock's composer — forwarded to
   *  {@link AssistantPanelProps.composerAttachments}. */
  composerAttachments?: AssistantPanelProps["composerAttachments"];
  /** Forwarded to {@link AssistantPanelProps.onComposerSend}. */
  onComposerSend?: AssistantPanelProps["onComposerSend"];
}

/** Visible, focusable descendants of a container, in tab order. Visibility is
 *  checked via getClientRects rather than offsetParent, which is null for
 *  position:fixed elements and would wrongly exclude them. */
function focusableWithin(container: HTMLElement): HTMLElement[] {
  const selector =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  return Array.from(container.querySelectorAll<HTMLElement>(selector)).filter(
    (el) => el.getClientRects().length > 0,
  );
}

export function AssistantDock({
  userId,
  navigate,
  balanceUsd = null,
  formatMoney,
  renderGraph,
  renderProviderIcon,
  onWorkflowMutation,
  onConnectRequirement,
  renderMarkdown,
  toolRenderers,
  renderConfirmedResult,
  renderTranscript,
  composerAttachments,
  onComposerSend,
}: AssistantDockProps) {
  const { open, openAssistant, closeAssistant, seed, clearSeed } =
    useAssistantLauncher();
  const chat = useAssistantChat(userId, {
    onWorkflowMutation,
    onConnectRequirement,
  });

  const isDesktop = useIsDesktop();
  const { width, maxWidth, setWidth, previewWidth, nudgeWidth } =
    usePanelWidth();

  const launcherRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);

  // Close on Escape — except when a popover inside the dialog is open (the
  // composer's model picker, …): that Escape belongs to the popover's own
  // close handling (`usePopover`), and closing the whole drawer under it would
  // drop the user's place in the conversation. A `usePopover` trigger carries
  // BOTH aria-haspopup and aria-expanded, which distinguishes it from the
  // transcript's expandable tool-card toggles (aria-expanded only) — those
  // must not suppress the drawer's own Escape-to-close.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (
        dialogRef.current?.querySelector(
          '[aria-haspopup="true"][aria-expanded="true"]',
        )
      ) {
        return;
      }
      closeAssistant();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, closeAssistant]);

  // Move focus into the dialog on open; restore it to the opener on close.
  useEffect(() => {
    if (open) {
      if (!wasOpenRef.current && !returnFocusRef.current) {
        returnFocusRef.current = document.activeElement as HTMLElement | null;
      }
      wasOpenRef.current = true;
      const el = dialogRef.current;
      if (el) (focusableWithin(el)[0] ?? el).focus();
    } else if (wasOpenRef.current) {
      wasOpenRef.current = false;
      const target = returnFocusRef.current?.isConnected
        ? returnFocusRef.current
        : launcherRef.current;
      target?.focus();
      returnFocusRef.current = null;
    }
  }, [open]);

  const openDialog = () => {
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    openAssistant();
  };

  if (!open) {
    return (
      <button
        ref={launcherRef}
        type="button"
        onClick={openDialog}
        aria-label="Open assistant"
        className="fixed right-4 bottom-4 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-[var(--shadow-overlay)] transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring"
      >
        <MessageSquare className="h-6 w-6" />
      </button>
    );
  }

  // Keep Tab focus within the dialog while it's open.
  const trapTab = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab") return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const focusables = focusableWithin(dialog);
    if (focusables.length === 0) {
      e.preventDefault();
      dialog.focus();
      return;
    }
    // Non-empty here (length === 0 returned above); the index access is safe.
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    const active = document.activeElement;
    const inside = active instanceof Node && dialog.contains(active);
    if (e.shiftKey) {
      if (!inside || active === first || active === dialog) {
        e.preventDefault();
        last.focus();
      }
    } else if (!inside || active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <>
      <div
        aria-hidden="true"
        className="fixed inset-0 z-40 bg-black/50"
        onClick={() => closeAssistant()}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-label="Assistant"
        aria-modal="true"
        tabIndex={-1}
        onKeyDown={trapTab}
        style={isDesktop ? { width: `${width}px` } : undefined}
        className="fixed inset-y-0 right-0 z-50 flex w-full flex-col border-card-edge border-l shadow-[var(--shadow-overlay)] focus:outline-none"
      >
        <AssistantPanel
          key={userId ?? "anon"}
          chat={chat}
          userId={userId}
          onClose={() => closeAssistant()}
          navigate={navigate}
          balanceUsd={balanceUsd}
          formatMoney={formatMoney}
          renderGraph={renderGraph}
          renderProviderIcon={renderProviderIcon}
          renderMarkdown={renderMarkdown}
          toolRenderers={toolRenderers}
          renderConfirmedResult={renderConfirmedResult}
          renderTranscript={renderTranscript}
          composerSeed={seed}
          onComposerSeedApplied={clearSeed}
          composerAttachments={composerAttachments}
          onComposerSend={onComposerSend}
        />
        {isDesktop && (
          <ResizeHandle
            width={width}
            maxWidth={maxWidth}
            onPreview={previewWidth}
            onCommit={setWidth}
            onNudge={nudgeWidth}
          />
        )}
      </div>
    </>
  );
}
